// Main preview popup orchestrator - refactored from popup.js

import { detectMeta } from '../../../../../modules/files/file-list.utils.js';
import {
	setPreviewState,
	getPreviewState,
	PREVIEW_POPUP_HIDE_DELAY
} from '../state.js';
import { savePreviewPosition } from '../position.js';
import { createPinButton, setupAutoPinHandlers, cleanupAutoPinHandlers } from '../pin.js';
import { buildPreviewUrl, updatePreviewContent } from '../content.js';
import { closeLinkPopup } from './link-popup.js';
import { setupPreviewPopupEvents } from './events.js';
import {
	renderPreviewPopupHtml,
	generatePreviewContent,
	positionPreviewPopup,
	setupOverscrollBehavior,
	restoreOverscrollBehavior
} from './render.js';

function disableFileLinks() {
	$('.file-link').each(function() {
		const $link = $(this);
		const originalHref = $link.attr('href');
		if (originalHref && !$link.data('original-href')) {
			$link.data('original-href', originalHref);
			$link.css('pointer-events', 'none');
			$link.attr('tabindex', '-1');
		}
	});
}

function enableFileLinks() {
	$('.file-link').each(function() {
		const $link = $(this);
		if ($link.data('original-href')) {
			$link.css('pointer-events', '');
			$link.removeAttr('tabindex');
			$link.removeData('original-href');
		}
	});
}

export function showPreviewPopup(file, fileKey) {
	if (window.innerWidth < 1200) return;

	const meta = detectMeta(file.p || file.path || '', file.n || file.name || '');
	const extLower = (meta.ext || '').toLowerCase();
	const isMP3 = extLower === 'mp3';
	if (isMP3) {
		return; // MP3 files are handled by audio-player.js
	}

	// Check file size limit (100 MB) for PDF and online files
	const fileSizeBytes = (typeof file.s !== 'undefined')
		? (Number(file.s) * 1000) // file.s is in KB
		: (typeof file.size !== 'undefined' ? Number(file.size) : 0);
	const maxPreviewSizeBytes = 100 * 1000 * 1000; // 100 MB
	const isPDF = extLower === 'pdf';
	const hasOnline = !!(file.o === 1 || file.online === 1);

	if ((isPDF || hasOnline) && fileSizeBytes > 0 && fileSizeBytes > maxPreviewSizeBytes) {
		console.warn('[Preview] File size exceeds 100MB limit:', fileSizeBytes / (1000 * 1000), 'MB');
		return; // Don't show preview for files larger than 100MB
	}

	const state = getPreviewState();
	const isSwitching = state.previewPopupElement && state.previewPopupElement.length;
	const wasPinned = isSwitching ? state.previewPopupPinned : false;
	// Preserve the triggering preview icon (set by the hover handler) — it is the
	// anchor for prev/next navigation. hidePreviewPopup() below clears the state,
	// so capture it now and re-attach after the popup is rebuilt.
	const triggerIcon = (state.currentPreviewFile && state.currentPreviewFile.$icon) ? state.currentPreviewFile.$icon : null;

	// Disable file links when preview popup is open to prevent navigation
	if (!isSwitching) {
		disableFileLinks();
	}

	if (isSwitching) {
		const rect = state.previewPopupElement[0].getBoundingClientRect();
		savePreviewPosition(rect.left, rect.top);
		hidePreviewPopup();
	} else {
		hidePreviewPopup();
		state.previewPopupPinned = false;
		setPreviewState(state);
	}

	const fileName = file.n || file.name || 'Unknown';
	const previewUrl = buildPreviewUrl(file);

	// Double-check: if previewUrl is empty (e.g., for MP3 files), don't show preview
	if (!previewUrl) {
		if (window.debugMode === '1') {
			console.log('[Preview] Preview URL is empty, skipping preview popup for:', fileName);
		}
		return;
	}

	const previewContent = generatePreviewContent(file, isPDF);
	const popupHtml = renderPreviewPopupHtml(fileName, previewContent);

	$('body').append(popupHtml);
	const newState = getPreviewState();
	newState.previewPopupElement = $('#preview-popup-overlay');
	newState.currentPreviewFile = { file, fileKey, $icon: triggerIcon };
	newState.previewPopupPinned = wasPinned;
	setPreviewState(newState);

	if (window.debugMode === '1') {
		const iframe = newState.previewPopupElement.find('iframe');
		console.log('[Preview] Iframe created:', {
			exists: iframe.length > 0,
			src: iframe.attr('src'),
			previewUrl: previewUrl
		});
	}

	const icon = newState.currentPreviewFile?.$icon;
	positionPreviewPopup(newState.previewPopupElement, icon, wasPinned);

	const pinButton = newState.previewPopupElement.find('.preview-popup__pin');
	const { setPinned } = createPinButton(pinButton);

	// Store setPinned callback in state for use when switching documents
	newState.setPinnedCallback = setPinned;

	// Setup overscroll behavior
	const overscrollValues = setupOverscrollBehavior();
	newState.originalHtmlOverscroll = overscrollValues.originalHtmlOverscroll;
	newState.originalBodyOverflow = overscrollValues.originalBodyOverflow;
	setPreviewState(newState);

	// Create close popup function
	const closePopup = () => {
		const currentState = getPreviewState();
		if (currentState.previewPopupElement && currentState.previewPopupElement.length && document.body.contains(currentState.previewPopupElement[0])) {
			const rect = currentState.previewPopupElement[0].getBoundingClientRect();
			savePreviewPosition(rect.left, rect.top);
		}
		$(document).off('mousemove.preview-drag mouseup.preview-drag mousemove.preview-resize mouseup.preview-resize keydown.preview-popup favorites:changed.previewFav preview:switched.previewFav preview:switched.previewNav');
		if (currentState.windowResizeHandler) {
			window.removeEventListener('resize', currentState.windowResizeHandler);
			currentState.windowResizeHandler = null;
		}
		if (currentState.navKeyHandler) {
			document.removeEventListener('keydown', currentState.navKeyHandler, true);
			currentState.navKeyHandler = null;
		}
		if (currentState.previewPopupElement && currentState.previewPopupElement.length && document.body.contains(currentState.previewPopupElement[0])) {
			const iframe = currentState.previewPopupElement.find('.preview-popup__iframe');
			cleanupAutoPinHandlers(iframe, currentState.previewPopupElement);
			// Clean up PDF blob URL if exists
			if (currentState.pdfBlobUrl) {
				URL.revokeObjectURL(currentState.pdfBlobUrl);
				currentState.pdfBlobUrl = null;
			}
			currentState.previewPopupElement.remove();
		}
		// Always clear state, even if element was already removed
		currentState.previewPopupElement = null;
		currentState.currentPreviewFile = null;
		currentState.previewPopupPinned = false;
		currentState.previewPopupHovered = false;
		currentState.previewIconHovered = false;
		currentState.setPinnedCallback = null;
		if (currentState.previewPopupTimeout) {
			clearTimeout(currentState.previewPopupTimeout);
			currentState.previewPopupTimeout = null;
		}
		if (currentState.previewHoverTimeout) {
			clearTimeout(currentState.previewHoverTimeout);
			currentState.previewHoverTimeout = null;
		}
		setPreviewState(currentState);
	};

	// Setup all event handlers
	setupPreviewPopupEvents(newState, closePopup, setPinned, hidePreviewPopup);

	// Load PDF with progress if PDF file
	if (isPDF) {
		updatePreviewContent(file, fileKey, setPinned);
	} else {
		const iframe = newState.previewPopupElement.find('.preview-popup__iframe');

		if (iframe.length > 0) {
			// Set iframe src
			iframe.attr('src', previewUrl);

			// Auto-click iframe when it loads to activate it so scroll works immediately
			let isProgrammaticClick = false;

			iframe.off('load.preview-init').on('load.preview-init', function() {
				newState.previewPopupElement.find('.preview-popup__loading').fadeOut(200);

				// Try to apply overscroll-behavior to iframe content (if same-origin)
				try {
					const iframeEl = iframe[0];
					if (iframeEl && iframeEl.contentWindow && iframeEl.contentWindow.document) {
						const iframeDoc = iframeEl.contentWindow.document;
						const iframeBody = iframeDoc.body;
						const iframeHtml = iframeDoc.documentElement;

						// Apply overscroll-behavior to prevent scroll chaining
						if (iframeBody) {
							iframeBody.style.overscrollBehavior = 'contain';
						}
						if (iframeHtml) {
							iframeHtml.style.overscrollBehavior = 'contain';
						}
					}
				} catch (e) {
					// Cross-origin restriction - can't access iframe content
					console.debug('[Preview] Cannot apply overscroll-behavior to iframe content (cross-origin):', e);
				}

				// Simulate click on iframe to activate it (like user clicking)
				setTimeout(() => {
					try {
						const iframeEl = iframe[0];

						// Set flag to prevent auto-pin on programmatic click
						isProgrammaticClick = true;

						// Focus iframe first
						iframeEl.focus();

						// Get iframe center coordinates
						const rect = iframeEl.getBoundingClientRect();
						const centerX = rect.left + rect.width / 2;
						const centerY = rect.top + rect.height / 2;

						// Create and dispatch click event at center of iframe
						const clickEvent = new MouseEvent('click', {
							bubbles: true,
							cancelable: true,
							view: window,
							clientX: centerX,
							clientY: centerY,
							button: 0
						});

						iframeEl.dispatchEvent(clickEvent);

						// Reset flag after a short delay
						setTimeout(() => {
							isProgrammaticClick = false;
						}, 100);

						// Also try to focus content window
						if (iframeEl.contentWindow) {
							iframeEl.contentWindow.focus();
						}
						
						// Return focus to preview popup element after activation
						setTimeout(() => {
							try {
								iframeEl.blur();
								const state = getPreviewState();
								if (state.previewPopupElement && state.previewPopupElement.length) {
									const popupEl = state.previewPopupElement[0];
									if (popupEl && document.body.contains(popupEl)) {
										popupEl.focus();
									}
								}
							} catch (e) {
								// Ignore focus errors
							}
						}, 100);
					} catch (e) {
						console.debug('[Preview] Could not auto-click iframe:', e);
						isProgrammaticClick = false;
					}
				}, 200);
			});
			
			// Add focus handler on iframe to return focus to popup immediately
			iframe.off('focus.preview-focus-return').on('focus.preview-focus-return', function() {
				setTimeout(() => {
					try {
						const state = getPreviewState();
						if (state.previewPopupElement && state.previewPopupElement.length) {
							const popupEl = state.previewPopupElement[0];
							if (popupEl && document.body.contains(popupEl)) {
								popupEl.focus();
							}
						}
					} catch (e) {
						// Ignore focus errors
					}
				}, 10);
			});
			
			// Add blur handler on iframe to return focus to popup
			iframe.off('blur.preview-focus-return').on('blur.preview-focus-return', function() {
				setTimeout(() => {
					try {
						const state = getPreviewState();
						if (state.previewPopupElement && state.previewPopupElement.length) {
							const popupEl = state.previewPopupElement[0];
							if (popupEl && document.body.contains(popupEl)) {
								const activeElement = document.activeElement;
								if (activeElement !== popupEl && !popupEl.contains(activeElement)) {
									popupEl.focus();
								}
							}
						}
					} catch (e) {
						// Ignore focus errors
					}
				}, 50);
			});
			
			// Add click handler on iframe to return focus to popup after user clicks
			iframe.off('click.preview-focus-return').on('click.preview-focus-return', function(e) {
				if (!e.isTrusted) return;
				
				setTimeout(() => {
					try {
						const state = getPreviewState();
						if (state.previewPopupElement && state.previewPopupElement.length) {
							const popupEl = state.previewPopupElement[0];
							if (popupEl && document.body.contains(popupEl)) {
								popupEl.focus();
							}
						}
					} catch (e) {
						// Ignore focus errors
					}
				}, 50);
			});

			iframe.on('error.preview-init', function() {
				console.error('[Preview] Iframe load error for URL:', previewUrl);
				newState.previewPopupElement.find('.preview-popup__loading').fadeOut(200);
			});

			iframe.on('load.preview-backup', function() {
				newState.previewPopupElement.find('.preview-popup__loading').fadeOut(200);
				if (window.debugMode === '1') {
					console.log('[Preview] Iframe loaded successfully:', previewUrl);
				}
			});

			// Setup auto-pin handlers for online document iframe
			setupAutoPinHandlers(iframe, newState.previewPopupElement, setPinned);

			// Setup wheel event handlers for iframe scrolling
			const contentDiv = newState.previewPopupElement.find('.preview-popup__content');
			contentDiv.off('wheel.preview-scroll').on('wheel.preview-scroll', function(e) {
				if (!isProgrammaticClick) {
					const state = getPreviewState();
					if (!state.previewPopupPinned && setPinned) {
						setPinned(true);
					}
				}
				e.stopPropagation();
			});
		}
	}

	// Focus preview popup element to ensure Escape works immediately
	setTimeout(() => {
		try {
			const popupEl = newState.previewPopupElement[0];
			if (popupEl && document.body.contains(popupEl)) {
				popupEl.focus();
			}
		} catch (e) {
			// Ignore focus errors
		}
	}, 50);

	setTimeout(() => newState.previewPopupElement.addClass('preview-popup-overlay--visible'), 10);
	newState.previewPopupElement.find('.preview-popup__loading').css('display', 'flex').show();

	if (window.debugMode === '1') {
		console.log('[Preview] Loading URL:', previewUrl, {
			file: { n: file.n, p: file.p, o: file.o, ot: file.ot },
			hasOnline: !!(file.o === 1 || file.online === 1),
			isOT: !!(file.ot || file.ot_level || (typeof file.category === 'string' && file.category.toUpperCase() === 'OT'))
		});
	}
}

export function hidePreviewPopup() {
	const state = getPreviewState();

	// Restore file links when preview popup is closed
	enableFileLinks();

	// Clear all timeouts first
	if (state.previewPopupTimeout) {
		clearTimeout(state.previewPopupTimeout);
		state.previewPopupTimeout = null;
	}
	if (state.previewHoverTimeout) {
		clearTimeout(state.previewHoverTimeout);
		state.previewHoverTimeout = null;
	}

	// Clean up event handlers
	$(document).off('mousemove.preview-drag mouseup.preview-drag mousemove.preview-resize mouseup.preview-resize keydown.preview-popup favorites:changed.previewFav preview:switched.previewFav preview:switched.previewNav');

	// Remove window-resize fail-safe handler if exists
	if (state.windowResizeHandler) {
		window.removeEventListener('resize', state.windowResizeHandler);
		state.windowResizeHandler = null;
		setPreviewState(state);
	}

	// Remove preview prev/next keyboard handler if exists
	if (state.navKeyHandler) {
		document.removeEventListener('keydown', state.navKeyHandler, true);
		state.navKeyHandler = null;
		setPreviewState(state);
	}

	// Remove document Escape handler if exists
	if (state.documentEscapeHandler) {
		document.removeEventListener('keydown', state.documentEscapeHandler, true);
		state.documentEscapeHandler = null;
		setPreviewState(state);
	}

	// Remove Escape handler from preview popup element
	if (state.previewPopupElement && state.previewPopupElement.length) {
		state.previewPopupElement.off('keydown.preview-popup-escape');
	}

	// Remove global wheel handler
	const currentState = getPreviewState();
	if (currentState.globalWheelHandler) {
		document.removeEventListener('wheel', currentState.globalWheelHandler, { capture: true });
		currentState.globalWheelHandler = null;
		setPreviewState(currentState);
	}

	// Restore html and body overscroll-behavior
	restoreOverscrollBehavior(state);

	// Reset hover states
	state.previewPopupHovered = false;
	state.previewIconHovered = false;

	// Clean up PDF blob URL if exists
	if (state.pdfBlobUrl) {
		URL.revokeObjectURL(state.pdfBlobUrl);
		state.pdfBlobUrl = null;
	}

	// Remove element if it exists in DOM
	if (state.previewPopupElement && state.previewPopupElement.length) {
		const elementExists = document.body.contains(state.previewPopupElement[0]);
		if (elementExists) {
			state.previewPopupElement.removeClass('preview-popup-overlay--visible');
			setTimeout(() => {
				const latestState = getPreviewState();
				// Double-check element still exists before removing
				if (latestState.previewPopupElement && latestState.previewPopupElement.length && 
					document.body.contains(latestState.previewPopupElement[0])) {
					latestState.previewPopupElement.remove();
				}
				// Always clear state
				latestState.previewPopupElement = null;
				latestState.currentPreviewFile = null;
				latestState.previewPopupPinned = false;
				latestState.setPinnedCallback = null;
				setPreviewState(latestState);
			}, 200);
		} else {
			// Element already removed from DOM, just clear state
			state.previewPopupElement = null;
			state.currentPreviewFile = null;
			state.previewPopupPinned = false;
			state.setPinnedCallback = null;
		}
	} else {
		// No element, ensure state is clean
		state.previewPopupElement = null;
		state.currentPreviewFile = null;
		state.previewPopupPinned = false;
		state.setPinnedCallback = null;
	}

	setPreviewState(state);
}

// Export closeLinkPopup for backward compatibility
export { closeLinkPopup };
